/**
 * File: userProfileService.ts
 * Description: Service for user profile API operations
 * Author: Archie Team
 * Created: 2026-05-11
 * Last Modified: 2026-05-11
 */

import { apiClient } from './apiClient';
import { KnowledgeBaseDocument } from './knowledgeBaseService';

const USER_PROFILE_ENDPOINT = '/api/user-profile';

export interface UserProfileResponse {
  id: number;
  username: string;
  email: string;
  display_name: string | null;
  persona: string | null;
  line_of_business: string | null;
  line_of_business_confirmed_at: string | null;
  ado_enabled: boolean;
  billy_enabled: boolean;
  /** Knowledge-base guidelines the user pre-selected for their project context. */
  selected_guidelines: KnowledgeBaseDocument[];
  /**
   * Whether the user's persona may review projects they own or co-own.
   * Mirrors the backend persona privilege matrix (e.g. Admins for testing).
   */
  can_self_review: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserProfileUpdateRequest {
  persona?: string | null;
  line_of_business?: string | null;
  ado_enabled?: boolean;
  billy_enabled?: boolean;
  selected_guidelines?: KnowledgeBaseDocument[];
}

export class UserProfileService {
  /**
   * Fetch the current user's profile.
   * @returns Promise with the user profile
   */
  static async getMyProfile(): Promise<UserProfileResponse> {
    return apiClient.get<UserProfileResponse>(`${USER_PROFILE_ENDPOINT}/me`);
  }

  /**
   * Update the current user's persona and line of business selections.
   * @param selection - The updated persona and line of business values
   * @returns Promise with the updated user profile
   */
  static async updateProfileSelection(
    selection: Pick<UserProfileUpdateRequest, 'persona' | 'line_of_business'>,
  ): Promise<UserProfileResponse> {
    return apiClient.patch<UserProfileResponse>(
      `${USER_PROFILE_ENDPOINT}/me`,
      selection,
    );
  }

  /**
   * Update the current user's Azure DevOps integration preference.
   * @param adoEnabled - Whether ADO integration should be enabled
   * @returns Promise with the updated user profile
   */
  static async updateAdoEnabled(
    adoEnabled: boolean,
  ): Promise<UserProfileResponse> {
    return apiClient.patch<UserProfileResponse>(`${USER_PROFILE_ENDPOINT}/me`, {
      ado_enabled: adoEnabled,
    });
  }

  /**
   * Update the current user's Billy business UI preference.
   * @param billyEnabled - Whether the Billy shortcut should be enabled
   * @returns Promise with the updated user profile
   */
  static async updateBillyEnabled(
    billyEnabled: boolean,
  ): Promise<UserProfileResponse> {
    return apiClient.patch<UserProfileResponse>(`${USER_PROFILE_ENDPOINT}/me`, {
      billy_enabled: billyEnabled,
    });
  }

  /**
   * Persist the user's pre-selected knowledge-base guidelines.
   * @param documents - The selected guideline documents
   * @returns Promise with the updated user profile
   */
  static async updateSelectedGuidelines(
    documents: KnowledgeBaseDocument[],
  ): Promise<UserProfileResponse> {
    return apiClient.patch<UserProfileResponse>(`${USER_PROFILE_ENDPOINT}/me`, {
      selected_guidelines: documents,
    });
  }

  /**
   * Fetch the valid line of business options from the backend.
   * @returns Promise with the list of options
   */
  static async getLineOfBusinessOptions(): Promise<string[]> {
    const response = await apiClient.get<{ options: string[] }>(
      `${USER_PROFILE_ENDPOINT}/line-of-business-options`,
    );
    return response.options;
  }
}
